/**
 * Sync reactivity, as thin counters.
 *
 * The mirror in SQLite is the truth; this store only says "something changed"
 * so hooks re-query it. One counter per record, one per record's insight
 * layout, one for the catalog, a count of pending writes, and a clock per
 * business of the last sync that landed anything. The sync engine bumps; hooks
 * select.
 *
 * Deliberately NOT persisted, and deliberately not drizzle's useLiveQuery:
 * counters were chosen over live queries so a re-query is always a conscious,
 * cheap read at a hook boundary, never a subscription per component.
 */
import type { EntryLinesWrite, EntryLinkOutcome, EntryLinkPosted, EntryLinkRefused, EntryProblem, EntryValues, EntryWarning } from '@alluzo/shared';
import { create } from 'zustand';

import { nextFailure, type SyncFailure, type SyncFailureKind } from '@/lib/sync-failure';
import type { DownloadProgress } from '@/lib/sync-progress';

/**
 * One write the server REFUSED, kept so somebody can be told about it.
 *
 * Not a sync failure, and deliberately not folded into `lastFailure`. Sync is
 * healthy in this case: the run completed, the server answered, and it said no
 * to one row. Folding the two would make a banner blame the network for a
 * permission, and `clearSyncFailure` on the next healthy run would erase the
 * only notice a person ever got that their entry is gone.
 *
 * A refused write is FINAL — `push` drops the op, because no retry mends a 4xx
 * — so this survives every later success and is cleared only when read.
 */
export type RefusedWrite = {
  readonly opId: string;
  readonly recordId: string;
  /** The server's own sentence. It names what was wrong and what to do. */
  readonly reason: string;
  readonly at: string;
  /**
   * What was refused, kept so the person can put it right and send it again
   * (B10 of the ledger live test): the entry the op wrote, whether it was a new
   * entry or a change to one the server holds, what it carried, and the
   * server's word on each field. A refused new entry is gone from the mirror,
   * so these are the only copy of what somebody typed. Absent on a refusal
   * noted before they were kept.
   */
  readonly entryId?: string;
  readonly kind?: 'create' | 'edit';
  readonly values?: EntryValues;
  readonly lines?: EntryLinesWrite;
  readonly problems?: readonly EntryProblem[];
};

/**
 * The rows one save's links posted, waiting to be said once ("Added to Till", L3 of docs/plans/links-fixes.md). `n` tells one save's toast from
 * the next. A host takes it when it shows it.
 */
export type LinkToast = { readonly n: number; readonly posted: readonly EntryLinkPosted[] };

/**
 * What one acked save's rules held on the server, said once as a warning:
 * a remote Must be block or a remote lock the server saved over (F10, F14 of
 * docs/plans/field-relations.md). A warn is not here: the form said it at Save.
 */
export type WarnToast = { readonly n: number; readonly messages: readonly string[] };

/** The warnings only the server can say: the ones it held, with a Needs you task. */
const HELD_CODES: ReadonlySet<EntryWarning['code']> = new Set(['ENTRY_RULE_HELD', 'FIELD_LOCKED_HELD', 'MONEY_RULE_HELD']);

/**
 * A link whose rule held on a save but which could not write its row. The
 * save stands; the row it should have posted does not exist. Kept, like a
 * refused write, until somebody has been shown it.
 */
export type RefusedLink = EntryLinkRefused & { readonly entryId: string; readonly at: string };

type SyncState = {
  /** recordId -> how many times its mirror changed. Hooks re-query on change. */
  recordVersions: Record<string, number>;
  /**
   * recordId -> how many times its insight layout changed.
   *
   * Separate from recordVersions on purpose. Adding, reordering or removing a
   * card changes nothing in the mirror, so bumping the record would tell the
   * entry list to re-read thousands of rows for a cosmetic edit. This counter
   * says precisely what moved.
   */
  insightVersions: Record<string, number>;
  /**
   * How many times ANY record's mirror changed.
   *
   * `recordVersions` answers "did this record change"; this answers "did
   * anything change", which is the question every business-wide number asks —
   * a sale in Sales moves the profit card. One number rather than a map, so a
   * hook can depend on it without re-deriving a token from an object every
   * render.
   */
  mirrorVersion: number;
  /**
   * How many times the BUSINESS insight strip changed.
   *
   * One counter, not one per key: a business has one strip. Separate from
   * `catalogVersion` because arranging the strip changes no record and should
   * not make home re-read its record list.
   */
  businessInsightVersion: number;
  /** Bumped when the catalog (or a today total) may have changed. */
  catalogVersion: number;
  /**
   * Outbox depth: writes saved locally and not yet accepted.
   *
   * One number, and it belongs to whichever business last pushed — every
   * writer of it now names a tenant. Nothing draws it yet; the per-business
   * badge that will reads `outboxCount(tenantId)` itself.
   */
  pendingCount: number;
  /**
   * tenantId -> the last sync of THAT business that landed anything.
   *
   * Per business and not one clock, because this is the loading gate: absent
   * means "the first sync is still in flight", which is the difference between
   * a spinner and an empty screen. A device-wide clock would tell a business
   * that has never synced here that it was up to date, and draw "no records"
   * over a business whose first pull had not arrived.
   */
  lastSyncedAt: Record<string, string>;
  /**
   * Why the last sync run failed, or null when the last one succeeded.
   *
   * The field that was missing. Without it an absent `lastSyncedAt` meant both
   * "still trying" and "failing since boot", and every screen drew a spinner
   * for the second case. See sync-failure.ts for the three states this makes
   * distinguishable.
   */
  lastFailure: SyncFailure | null;
  /**
   * Writes the server refused and `push` therefore dropped, newest last.
   *
   * The gap this closes: a refused entry used to disappear in silence. `push`
   * wrote the reason into `outbox.failed_reason` and `removePending` deleted
   * that row on the next line, then the drain returned normally, so the run
   * counted as a success and `clearSyncFailure` wiped the banner. A person
   * typed a sale, watched it save, and found it gone.
   */
  refusedWrites: RefusedWrite[];
  /** Saves whose links posted something, not yet said. Oldest first. */
  linkToasts: LinkToast[];
  /** Saves the server held a rule or a lock on, not yet said. Oldest first. */
  warnToasts: WarnToast[];
  /** Links that held but could not write, newest last. Cleared only when read. */
  refusedLinks: RefusedLink[];
  syncing: boolean;
  /**
   * The long download in flight, or null. Set only for a run of more than one
   * page, so an ordinary delta pull draws nothing. See sync-progress.ts.
   */
  download: DownloadProgress | null;

  bumpRecord: (recordId: string) => void;
  bumpInsights: (recordId: string) => void;
  bumpBusinessInsights: () => void;
  bumpCatalog: () => void;
  setPendingCount: (count: number) => void;
  setSyncing: (syncing: boolean) => void;
  setDownload: (download: DownloadProgress | null) => void;
  stampSynced: (tenantId: string) => void;
  noteSyncFailure: (failure: {
    kind: SyncFailureKind;
    status: number | null;
    message: string;
  }) => void;
  clearSyncFailure: () => void;
  noteRefusedWrite: (refused: Omit<RefusedWrite, 'at'>) => void;
  /** Called when a person has been shown them. Nothing else may clear these. */
  clearRefusedWrites: () => void;
  /**
   * One refusal is in a person's hands now: the form that made it shows its
   * problems, or it was sent again or discarded. Only that one goes.
   */
  takeRefusedWrite: (opId: string) => void;
  /** What one acked save's links did: a toast for what posted, a notice for what could not. */
  noteLinks: (entryId: string, outcome: EntryLinkOutcome) => void;
  /** The host showed it, or let it go. */
  takeLinkToast: (n: number) => void;
  /** What one acked save's rules held on the server: a warning for each, said once. */
  noteWarnings: (entryId: string, warnings: readonly EntryWarning[]) => void;
  /** The host said it. */
  takeWarnToast: (n: number) => void;
  /** Called when a person has been shown them. */
  clearRefusedLinks: () => void;
  /**
   * Back to boot. Sign-out clears the device tables; these counters are the
   * same state held in memory, so they go with them. Without this the next
   * account reads the previous one's pending count on its first frame.
   */
  reset: () => void;
};

/** Every link toast's own number, never reused, so a host keys one toast apart from the next. */
let linkToastSeq = 0;

/** Every warn toast's own number, never reused. */
let warnToastSeq = 0;

export const useSyncStore = create<SyncState>()((set) => ({
  recordVersions: {},
  insightVersions: {},
  mirrorVersion: 0,
  businessInsightVersion: 0,
  catalogVersion: 0,
  pendingCount: 0,
  lastSyncedAt: {},
  lastFailure: null,
  refusedWrites: [],
  linkToasts: [],
  warnToasts: [],
  refusedLinks: [],
  syncing: false,
  download: null,

  bumpRecord: (recordId) =>
    set((state) => ({
      mirrorVersion: state.mirrorVersion + 1,
      recordVersions: {
        ...state.recordVersions,
        [recordId]: (state.recordVersions[recordId] ?? 0) + 1,
      },
    })),
  bumpInsights: (recordId) =>
    set((state) => ({
      insightVersions: {
        ...state.insightVersions,
        [recordId]: (state.insightVersions[recordId] ?? 0) + 1,
      },
    })),
  bumpBusinessInsights: () =>
    set((state) => ({ businessInsightVersion: state.businessInsightVersion + 1 })),
  bumpCatalog: () => set((state) => ({ catalogVersion: state.catalogVersion + 1 })),
  setPendingCount: (count) => set({ pendingCount: count }),
  setSyncing: (syncing) => set({ syncing }),
  // Already-null stays the same object, so the end of a quiet pull re-renders nothing.
  setDownload: (download) => set((state) => (download === null && state.download === null ? state : { download })),
  stampSynced: (tenantId) =>
    set((state) => ({
      lastSyncedAt: { ...state.lastSyncedAt, [tenantId]: new Date().toISOString() },
    })),
  noteSyncFailure: (failure) =>
    set((state) => ({
      lastFailure: nextFailure(state.lastFailure, { ...failure, at: new Date().toISOString() }),
    })),
  // Already-null stays the same object, so a healthy sync re-renders nothing.
  clearSyncFailure: () => set((state) => (state.lastFailure === null ? state : { lastFailure: null })),
  noteRefusedWrite: (refused) =>
    set((state) =>
      // One notice per op. `push` drops a refused op, so a second note for the
      // same one would be a bug elsewhere rather than a second refusal.
      state.refusedWrites.some((one) => one.opId === refused.opId)
        ? state
        : { refusedWrites: [...state.refusedWrites, { ...refused, at: new Date().toISOString() }] },
    ),
  clearRefusedWrites: () =>
    set((state) => (state.refusedWrites.length === 0 ? state : { refusedWrites: [] })),
  takeRefusedWrite: (opId) =>
    set((state) =>
      state.refusedWrites.some((one) => one.opId === opId)
        ? { refusedWrites: state.refusedWrites.filter((one) => one.opId !== opId) }
        : state,
    ),
  noteLinks: (entryId, outcome) =>
    set((state) => {
      // Only what can be said by name: a record this person may not open is not named to them.
      const posted = outcome.posted.filter((one) => one.toRecordName !== null);
      const at = new Date().toISOString();
      // One notice per link per entry: the newest refusal of it replaces the one before.
      const refused = outcome.refused.map((one): RefusedLink => ({ ...one, entryId, at }));
      const kept = state.refusedLinks.filter((was) => !refused.some((one) => one.entryId === was.entryId && one.linkId === was.linkId));
      return {
        linkToasts: posted.length === 0 ? state.linkToasts : [...state.linkToasts, { n: ++linkToastSeq, posted }],
        refusedLinks: refused.length === 0 ? state.refusedLinks : [...kept, ...refused],
      };
    }),
  takeLinkToast: (n) =>
    set((state) => (state.linkToasts.some((one) => one.n === n) ? { linkToasts: state.linkToasts.filter((one) => one.n !== n) } : state)),
  noteWarnings: (_entryId, warnings) =>
    set((state) => {
      const messages = warnings.filter((one) => HELD_CODES.has(one.code)).map((one) => one.message);
      return messages.length === 0 ? state : { warnToasts: [...state.warnToasts, { n: ++warnToastSeq, messages }] };
    }),
  takeWarnToast: (n) =>
    set((state) => (state.warnToasts.some((one) => one.n === n) ? { warnToasts: state.warnToasts.filter((one) => one.n !== n) } : state)),
  clearRefusedLinks: () => set((state) => (state.refusedLinks.length === 0 ? state : { refusedLinks: [] })),
  reset: () =>
    set({
      recordVersions: {},
      insightVersions: {},
      mirrorVersion: 0,
      businessInsightVersion: 0,
      catalogVersion: 0,
      pendingCount: 0,
      lastSyncedAt: {},
      lastFailure: null,
      refusedWrites: [],
      linkToasts: [],
      warnToasts: [],
      refusedLinks: [],
      syncing: false,
      download: null,
    }),
}));

/**
 * When this business last synced anything here, or null when it never has.
 *
 * A hook rather than a map read at each call site, so what a screen depends on
 * is one string and not the whole map: a pull landing for business B must not
 * re-render every screen open on business A.
 */
export const useLastSyncedAt = (tenantId: string | null): string | null =>
  useSyncStore((state) => (tenantId === null ? null : (state.lastSyncedAt[tenantId] ?? null)));
